/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { rest } from 'msw';
import { ProductIds } from '@/common/enums';
import { CompanyUser } from '@/views/companies/interfaces';
import { CUSTOM_DIMENSIONS, DEFAULT_CUSTOM_DIMENSIONS } from '@/mocks/data/creativeCanvasDetails';
import { Creative, CreativeFolder, CreativeForm, CreativeTemplate, TemplateCreate } from '@/views/creatives/interfaces';
import { filterByKeys, filterByName, findById, mockServerApi, randomId, sortItems } from '@/common/utils';
import { COMPANY_USERS_LIST, CREATIVES_LIST, CREATIVE_FOLDERS, CREATIVE_PLAY,
  CREATIVE_PLAY_NEW, CREATIVE_CANVAS_BACKGROUND, USERS, CREATIVE_BASE, TEMPLATES, COMPANY_TEMPLATES, CRAFTSMAN_TEMPLATES, COMPANIES_LIST } from '@/mocks/data';

let currentCompany = null;
let currentCustomDimensionId = 4;

const CREATIVES = CREATIVES_LIST.map((item, index) => ({
  ...item,
  creator: (index % 2 === 0) ? COMPANY_USERS_LIST[0] : COMPANY_USERS_LIST[1],
}));

const FAVORITES = [CREATIVES[0]];

const FOLDERS = CREATIVE_FOLDERS.map((folder) => ({
  ...folder,
  creator: COMPANY_USERS_LIST[0],
  // hasFolders: filterByKeys([...CREATIVE_FOLDERS], ['parentId'], folder.id).length > 0,
}));

export const creativesRequests = (msw: typeof rest) => [

  msw.get(mockServerApi('/creatives/dimensions'), (req, res, ctx) => {
    const urlSearchParams = new URLSearchParams(req.url.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if ('q' in params) {
      const value = params.q.split(':').pop();
      if (value === 'cre_ko2BK6') {
        return res((ctx.status(200), ctx.json({
          items: [],
          page: 1,
          total: 0,
        })));
      }
    }
    return res((ctx.status(200), ctx.json({
      items: CUSTOM_DIMENSIONS,
      page: CUSTOM_DIMENSIONS.length,
      total: 0,
    })));
  }),

  msw.post(mockServerApi('/creatives/dimensions'), (req: { params: { id: string }, body: {[key:string]: string} }, res, ctx) => {
    currentCustomDimensionId += 1;
    const { height, name, width } = req.body;
    return res((ctx.status(200), ctx.json({ ...DEFAULT_CUSTOM_DIMENSIONS, id: currentCustomDimensionId, height, width, name })));
  }),

  msw.put(mockServerApi('/creatives/dimensions/:id'), (req, res, ctx) => { res((ctx.status(200), ctx.json([CUSTOM_DIMENSIONS]))); }),

  msw.delete(mockServerApi('/creatives/dimensions/:id'), (req, res, ctx) => res((ctx.status(200), ctx.json({})))),

  msw.get(mockServerApi('/creatives/templates'), (req, res, ctx) => {
    const urlSearchParams = new URLSearchParams(req.url.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    let items: Creative[] = COMPANY_TEMPLATES
      .filter((item) => (!item.deletedAt) && item.creator.companies.some((c) => c.id === params.companies));

    if ('includes' in params) {
      // Set current company
      currentCompany = params.companies;

      if ('q' in params) {
        const value = params.q.split(':').pop();
        items = filterByName(items, value);
      }
    }

    return res(
      ctx.status(200),
      ctx.json({
        items,
        templates: CRAFTSMAN_TEMPLATES,
        page: 1,
        total: 2,
        limit: 500,
      }),
    );
  }),

  msw.post(mockServerApi('/creatives/templates'), (req: { body: TemplateCreate }, res, ctx) => {
    const { companyId, creativeId } = req.body;

    const templatesList = (companyId) ? COMPANY_TEMPLATES : CRAFTSMAN_TEMPLATES;
    const item = CREATIVES.find((n) => n.id === creativeId.toString()) as Creative;

    if (item.id) {
      const newItem = {
        ...item,
        id: randomId(),
        isTemplate: true,
        creator: {
          ...item.creator,
          companies: [
            (companyId) ? { ...COMPANIES_LIST.find((c) => c.id === companyId) } : COMPANIES_LIST[0],
          ],
        },
      };

      templatesList.push(newItem);
    }

    return res(
      ctx.status(200),
      ctx.delay(500),
      ctx.json({
        id: COMPANY_TEMPLATES.at(-1).id,
      }),
    );
  }),

  msw.get(mockServerApi('/creatives'), (req, res, ctx) => {
    const urlSearchParams = new URLSearchParams(req.url.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    // used for canvas CreativeNewForm 'From existing ad' selection only for craftsman+ company
    if ('type' in params && params.companies === '123') {
      const items = CREATIVES_LIST.filter((item) => item.type?.id === ProductIds.CANVAS);
      return res(
        ctx.status(200),
        ctx.json({
          folders: [],
          items: items.filter((creative) => !creative.isArchived && !creative.deletedAt),
        }),
      );
    }

    // return empty list if not craftsman+ company
    if ('companies' in params && params.companies === '789') {
      return res(
        ctx.status(200),
        ctx.json({
          folders: [],
          items: [],
        }),
      );
    }

    if ('includes' in params) {
      // Set current company
      currentCompany = params.companies;

      let items = CREATIVES.filter((creative) => !creative.isArchived && !creative.deletedAt);

      const folderItems = FOLDERS.filter((folder) => folder.parentId === params.folderId);

      if ('archived' in params) {
        items = CREATIVES.filter((creative) => creative.isArchived && !creative.deletedAt);
      }

      if ('deleted' in params) {
        items = CREATIVES.filter((creative) => creative.deletedAt);
      }

      if ('folderId' in params) {
        // Disable creatives filter by folderId on search inputs
        if ('q' in params) {
          const value = params.q.split(':').pop();
          items = filterByName(items, value);
        } else {
          // Filter by id
          items = items.filter((creative) => creative.folderId === params.folderId);
        }
      }

      if ('q' in params) {
        const value = params.q.split(':').pop();
        items = filterByName(items, value);
      }

      if ('order' in params) {
        const [key, value] = params.order.split(':');
        items = sortItems(items, { sortBy: key, desc: value === 'desc' });
      }

      return res(
        ctx.status(200),
        ctx.json({
          folders: folderItems,
          items,
        }),
      );
    }

    return res(
      ctx.status(200),
      ctx.json({
        folders: FOLDERS,
        items: CREATIVES.filter((creative) => !creative.isArchived && !creative.deletedAt),
      }),
    );
  }),

  msw.get(mockServerApi('/creatives/:id'), (req, res, ctx) => {
    const { id } = req.params;
    const creative = findById(CREATIVES, id.toString());

    return res(
      ctx.status(200),
      ctx.json(creative),
    );
  }),

  msw.patch(mockServerApi('/creatives/:id'), (req: { params: { id: string }, body: Creative }, res, ctx) => {
    const { folderId, companyId, isGlobal, isTemplate, name } = req.body;

    let index = -1;
    let newItem = null;

    if (isTemplate) {
      // Check if is global item
      const array = isGlobal ? CRAFTSMAN_TEMPLATES : COMPANY_TEMPLATES;
      index = array.findIndex((t) => t.id === req.params.id);
      newItem = array[index];
      // Update template name
      newItem.name = name;
    } else {
      index = CREATIVES.findIndex((item) => item.id === req.params.id);
      newItem = { ...CREATIVES[index] };
      CREATIVES[index] = newItem;
      // Update parent/folder id
      newItem.folderId = folderId || 'null';

      if (folderId && folderId !== 'null') {
        const folderIndex = FOLDERS.findIndex((item) => item.id === folderId);
        FOLDERS[folderIndex].updatedAt = new Date(Date.now()).getTime();
      }

      if (companyId && (companyId !== currentCompany)) {
        // Remove from CREATIVES
        CREATIVES.splice(index, 1);
      }
    }

    newItem.updatedAt = new Date(Date.now()).getTime();

    return res(
      ctx.status(200),
      ctx.json({
        ...newItem,
        ...req.body,
      }),
    );
  }),

  msw.get(mockServerApi('/me/favorites'), (req, res, ctx) => res(
    ctx.status(200),
    ctx.json({
      items: FAVORITES,
    }),
  )),

  msw.post(mockServerApi('/creatives/:id/favorite'), (req, res, ctx) => {
    const { id } = req.params;
    const item = CREATIVES.find((n) => n.id === id.toString());
    FAVORITES.push(item);
    return res(
      ctx.status(200),
      ctx.json({
        message: 'ok',
      }),
    );
  }),

  msw.delete(mockServerApi('/creatives/:id/favorite'), (req, res, ctx) => {
    const { id } = req.params;
    const index = FAVORITES.findIndex((n) => n.id === id);
    FAVORITES.splice(index, 1);
    return res(
      ctx.status(200),
      ctx.json({
        message: 'ok',
      }),
    );
  }),

  msw.delete(mockServerApi('/creatives/:id'), (req, res, ctx) => {
    const { id } = req.params;
    const urlSearchParams = new URLSearchParams(req.url.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    let array: CreativeTemplate[] = CREATIVES;

    if ('isTemplate' in params) {
      const isGlobal = 'isGlobal' in params;
      array = isGlobal ? CRAFTSMAN_TEMPLATES : COMPANY_TEMPLATES;
    }

    const index = array.findIndex((n) => n.id === id.toString());
    // Remove item
    array[index].deletedAt = Date.now();

    return res(
      ctx.delay(500),
      ctx.status(204),
    );
  }),

  msw.post(mockServerApi('/creatives/:id/archive'), (req, res, ctx) => {
    const { id } = req.params;
    const index = CREATIVES.findIndex((n) => n.id === id.toString());

    CREATIVES[index].archivedAt = Date.now();
    CREATIVES[index].isArchived = !CREATIVES[index].isArchived;

    return res(
      ctx.status(200),
      ctx.json({
        message: 'ok',
        isArchived: CREATIVES[index].isArchived,
      }),
    );
  }),

  msw.post(mockServerApi('/creatives/:id/restore'), (req, res, ctx) => {
    const { id } = req.params;
    const index = CREATIVES.findIndex((n) => n.id === id.toString());

    CREATIVES[index].deletedAt = null;

    return res(
      ctx.status(200),
      ctx.json({
        message: 'ok',
      }),
    );
  }),

  msw.post(mockServerApi('/creatives/:id/share'), (req, res, ctx) => {
    const creative = findById(CREATIVES, (req.params.id as string));

    return res(
      ctx.status(200),
      ctx.json(creative),
    );
  }),

  msw.get(mockServerApi('/types/templates'), (req, res, ctx) => res(
    ctx.status(200),
    ctx.json({
      items: TEMPLATES,
    }),
  )),

  msw.post(mockServerApi('/creatives'), (req: { body: CreativeForm }, res, ctx) => {
    let newCreative;

    const productId = req.body?.productId;

    if (!req.body.templateId) { // Custom creative
      if (productId === ProductIds.PLAY) newCreative = CREATIVE_PLAY_NEW;
      else if (productId === ProductIds.CANVAS) newCreative = CREATIVE_CANVAS_BACKGROUND;
      else newCreative = CREATIVE_BASE;
    } else {
      newCreative = CREATIVE_PLAY; // Creative with existing template
    }

    newCreative = {
      ...newCreative,
      id: randomId(),
      name: req.body.name,
      creator: findById(USERS, req.body.userId),
      createdAt: new Date(Date.now()).getTime(),
      updatedAt: null,
    };

    CREATIVES.push(newCreative);

    return res(
      ctx.status(200),
      ctx.json(newCreative),
    );
  }),

  msw.post(mockServerApi('/creatives/:id/duplicate'), (req, res, ctx) => {
    const currentUser = USERS.find((user) => user.email === window.localStorage.getItem('email'));
    const currentCreative = findById(CREATIVES, req.params.id.toString()) as Creative;

    // TODO => Update type
    const newCreative: any = {
      ...currentCreative,
      id: randomId(),
      creator: (req.url.toString().includes('includes=creator')) ? currentUser : currentCreative.creator as CompanyUser,
    };

    CREATIVES.push(newCreative);

    return res(
      ctx.status(201),
      ctx.json(newCreative),
    );
  }),

  msw.post(mockServerApi('/creatives/folders'), (req: { body: Partial<CreativeFolder> }, res, ctx) => {
    const { name, parentId, companyId, userId } = req.body;

    const creativeFolder = {
      id: `fol_${randomId()}`,
      name,
      parentId,
      companyId,
      createdAt: new Date(Date.now()).getTime(),
      updatedAt: null,
      creator: filterByKeys(USERS, ['id'], userId)[0],
    };

    FOLDERS.push(creativeFolder);

    return res(
      ctx.status(200),
      ctx.json(creativeFolder),
    );
  }),

  msw.patch(mockServerApi('/creatives/folders/:id'), (req: { params: { id: string }, body: Partial<CreativeFolder> }, res, ctx) => {
    const { parentId, name } = req.body;
    // Find item
    const folderIndex = FOLDERS.findIndex((folder) => folder.id === req.params.id as string);
    const newItem = { ...FOLDERS[folderIndex] };
    // Update parent/folder id
    newItem.parentId = (parentId) || 'null';
    newItem.updatedAt = new Date(Date.now()).getTime();
    // Update name
    if (name) newItem.name = name;

    if (parentId !== 'null') {
      const parentIndex = FOLDERS.findIndex((item) => item.id === parentId);
      FOLDERS[parentIndex].updatedAt = new Date(Date.now()).getTime();
    }

    FOLDERS[folderIndex] = newItem;

    return res(
      ctx.status(200),
      ctx.json({
        ...newItem,
        ...req.body,
      }),
    );
  }),

  // TODO => Check delete behavior
  msw.delete(mockServerApi('/creatives/folders/:id'), (req, res, ctx) => {
    const { id } = req.params;
    const index = FOLDERS.findIndex((n) => n.id === id.toString());

    // Check folder content
    const items = CREATIVES.filter((creative) => creative.folderId === id);
    const folderItems = FOLDERS.filter((folder) => folder.parentId === id);

    if (items.length > 0 || folderItems.length > 0) {
      return res(
        ctx.status(400),
        ctx.json({
          message: 'Cannot delete folder that contains items',
        }),
      );
    }

    folderItems.splice(index, 1);

    return res(
      ctx.status(204),
    );
  }),

  msw.post(mockServerApi('/creatives/:id/export'), (req, res, ctx) => res(
    ctx.status(200),
    ctx.json({
      src: '',
    }),
  )),

  msw.post(mockServerApi('/creatives/:id/preview'), (req, res, ctx) => res(
    ctx.status(200),
    ctx.json({
      // eslint-disable-next-line max-len
      src: 'https://craftsman-plus.com/preview/?portrait=true&playable=https://s3.us-east-1.amazonaws.com/production-elasticbeanstalk-environmen-databucket-1v9icak6ecq6y/1646170276585-1646170276585.html',
    }),
  )),

  msw.post(mockServerApi('/creatives/:id/size'), (req, res, ctx) => res(
    ctx.status(200),
    ctx.json({
      videos: [],
      images: [],
      fonts: [],
      audio: [],
      engine: {
        gameCode: 78125,
        partnerCode: 302470,
      },
      config: {
        size: 282,
      },
    }),
  )),

  msw.get(mockServerApi('/creatives/snapshots'), (req, res, ctx) => res(
    ctx.status(200),
    ctx.json({
      items: [{
        id: 1,
        createdAt: '2022-02-07T17:54:15.000Z',
      }],
      page: 1,
      total: 1,
      limit: 15,
    }),
  )),

  msw.get(mockServerApi('/creatives/history/:id'), (req, res, ctx) => res(
    ctx.status(200),
    ctx.json({
      id: 1,
      creativeId: 'webBjUFhNe',
      createdAt: '2022-02-07T17:54:15.000Z',
      settings: {},
      scenes: {},
    }),
  )),

  msw.get(mockServerApi('/creatives/exports'), (req, res, ctx) => res(
    ctx.status(200),
    ctx.json({
      items: [{
        id: 1,
        createdAt: '2022-02-07T17:54:15.000Z',
      }],
      page: 1,
      total: 1,
      limit: 15,
    }),
  )),
];

export default creativesRequests;
